import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import type { ReactNode } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { useCreateProject, useCustomerLookup, useMe } from '@/api';

/**
 * The "New project" modal, following the webapp's
 * (`Web/src/components/global/NewProjectModal/`) with deliberate omissions.
 *
 * Gone: the Customer-project/Internal radio and the Billing method select.
 * Neither has anywhere to write to here — a project carries no type at all, and
 * billing method is a property of a CONTRACT (`fixed | tm | retainer`), chosen
 * on Financials where the contract is created. Asking for them at creation
 * would be asking a question this module cannot store the answer to.
 *
 * Gone too, since 2026-10-08 (ADR 0047): the "Where do you want to start?"
 * choice. One action, "Create project", which lands on Financials (the pilot's
 * ask, the same day): the contract is what a new project is set up with first.
 * Planning is one click away in the area nav.
 */

/**
 * Label above the control, never inside it: the theme pins `MuiInputLabel`
 * to `shrink: false` and `MuiOutlinedInput` to `notched: false`, so MUI's
 * floating label would sit ON TOP of the value rather than rising out of the
 * way. The design system puts form labels above the field — the same shape
 * `resourceplanning/FindAllocateDrawer` uses.
 */
const Field = ({
  id,
  label,
  required,
  children,
}: {
  id: string;
  label: string;
  required?: boolean;
  children: ReactNode;
}) => (
  <Stack gap={0.5}>
    <Typography
      variant="label"
      component="label"
      htmlFor={id}
      sx={{ display: 'block', color: 'var(--fg-2)' }}
    >
      {label}
      {required ? (
        <Box component="span" aria-hidden sx={{ color: 'var(--color-error-80)', ml: 0.25 }}>
          *
        </Box>
      ) : null}
    </Typography>
    {children}
  </Stack>
);

export type NewProjectDialogProps = {
  open: boolean;
  onClose: () => void;
};

export const NewProjectDialog = ({ open, onClose }: NewProjectDialogProps) => {
  const { t } = useTranslation(['pmPortfolio', 'pmCommon']);
  const [name, setName] = useState('');
  const [customer, setCustomer] = useState<{ tlpId: string; name: string } | null>(null);
  const navigate = useNavigate();
  const createProject = useCreateProject();

  const me = useMe();
  const lookupsEnabled = (me.data?.features.lookups ?? 'off') !== 'off';
  const customerLookup = useCustomerLookup('', lookupsEnabled);
  const customerOptions = customerLookup.data?.items ?? [];
  /*
    A project always belongs to someone: in TimeLog it cannot be created without
    a customer, so the project rail's meta line would never be blank there.
    Locally it could — nothing sent a customer, and every project landed with an
    empty meta row. Defaulting to the first customer the lookup offers
    guarantees a name even when whoever creates the project ignores the field.

    With `LOOKUP_MODE=off` there is nothing to offer and the field is disabled:
    the one case where a project is still created without a customer. Blocking
    creation instead would make credential-less dev impossible.
  */
  const effectiveCustomer = customer ?? customerOptions[0] ?? null;

  const reset = () => {
    setName('');
    setCustomer(null);
    createProject.reset();
  };

  const close = () => {
    reset();
    onClose();
  };

  const submit = () => {
    createProject.mutate(
      {
        name: name.trim(),
        ...(effectiveCustomer
          ? {
              customer: {
                tlpId: effectiveCustomer.tlpId,
                snapshot: { name: effectiveCustomer.name },
                capturedAt: new Date().toISOString(),
              },
            }
          : {}),
      },
      {
        onSuccess: (created) => {
          reset();
          onClose();
          void navigate(`/projects/${created.id}/financials`);
        },
      },
    );
  };

  const canSubmit = name.trim().length > 0 && !createProject.isPending;

  return (
    <Dialog
      open={open}
      onClose={createProject.isPending ? undefined : close}
      maxWidth="xs"
      fullWidth
      aria-labelledby="new-project-dialog-title"
    >
      {/*
        The automation id goes on real markup INSIDE the paper. On `<Dialog>` it
        lands on the Modal root, which spans the viewport — it answers "is it
        open?" and never "where is it?".
      */}
      <DialogTitle id="new-project-dialog-title" data-automation-id="new-project-dialog">
        {t('newProject.Title')}
      </DialogTitle>
      <DialogContent>
        <Stack gap={2} sx={{ pt: 1 }}>
          <Field id="new-project-name" label={t('newProject.fields.ProjectName')} required>
            <TextField
              id="new-project-name"
              size="small"
              required
              autoFocus
              value={name}
              onChange={(event) => setName(event.target.value)}
              fullWidth
              slotProps={{ htmlInput: { 'data-automation-id': 'new-project-name-input' } }}
            />
          </Field>

          <Field
            id="new-project-customer"
            label={t('pmCommon:term.Customer')}
            required={lookupsEnabled}
          >
            <Autocomplete
              id="new-project-customer"
              size="small"
              options={customerOptions}
              value={effectiveCustomer}
              disabled={!lookupsEnabled}
              onChange={(_event, next) =>
                setCustomer(next ? { tlpId: next.tlpId, name: next.name } : null)
              }
              getOptionLabel={(option) => option.name}
              isOptionEqualToValue={(option, selected) => option.tlpId === selected.tlpId}
              loading={customerLookup.isFetching}
              noOptionsText={t('newProject.fields.NoCustomers')}
              renderInput={(params) => (
                <TextField
                  {...params}
                  helperText={lookupsEnabled ? undefined : t('newProject.fields.CustomerLookupOff')}
                  slotProps={{
                    ...params,
                    htmlInput: {
                      ...params.inputProps,
                      'data-automation-id': 'new-project-customer-input',
                    },
                  }}
                />
              )}
            />
          </Field>

          {createProject.error ? (
            <Alert severity="error" data-automation-id="new-project-error">
              {createProject.error.message}
            </Alert>
          ) : null}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={close}
          disabled={createProject.isPending}
          color="inherit"
          data-automation-id="new-project-cancel"
        >
          {t('newProject.actions.Cancel')}
        </Button>
        <Button
          onClick={submit}
          disabled={!canSubmit}
          variant="contained"
          data-automation-id="new-project-create"
        >
          {t('newProject.actions.Create')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
